import { borgAnalytics, Button, getChannel, useTheme } from '@ytl/common-web';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as CameraIcon } from '../../../../assets/camera.svg';
import { ReactComponent as PhonebookIcon } from '../../../../assets/phonebook.svg';
import { ReactComponent as UserIcon } from '../../../../assets/user_profil.svg';
import { ReactComponent as WarningIcon } from '../../../../assets/warning.svg';
import { useLanguage } from '../../../../contexts/languageContext';
import useRecaptcha from '../../../../modules/useRecaptcha';
import { getConfig } from '../../../data/config';
import { gripResource } from '../../../resource/grip.resource';
import { RecaptchaPortal } from '../../RecaptchaPortal';

export const KYCApp = () => {
  const { translation, language } = useLanguage();
  const { isWebView } = useTheme();
  const navigate = useNavigate();
  const {
    recaptchaRef,
    recaptchaForceRender,
    recaptchaLang,
    handleRecaptcha,
    captchaToken,
    recaptchaSiteKey,
    recaptchaInvisible,
    executeInvisibleCaptcha,
    needsCaptcha,
  } = useRecaptcha();
  const waitingForCaptchaRef = useRef(false);

  const privacyUrl = `${getConfig().dapExternalUriBaseUrl}?uri=${getConfig().privacyPolicyUrl}`;

  const handleBack = () => {
    navigate('/onboarding?app=true');
  };

  useLayoutEffect(() => {
    document.documentElement.classList.add('kyc-init-page');

    return () => {
      document.documentElement.classList.remove('kyc-init-page');
    };
  }, []);

  useEffect(() => {
    borgAnalytics.sendVirtualPageView({
      channel: getChannel(isWebView),
      virtualName: 'Kyc authentication start',
    });
  }, [isWebView]);

  const startIdentification = useCallback(async () => {
    const externalId = crypto.randomUUID();

    const urlOk = new URL(getConfig().kycRedirectUrlOk);
    urlOk.searchParams.set('app', 'true');
    urlOk.searchParams.set('locale', language);
    urlOk.searchParams.set('externalId', externalId);

    const urlNok = new URL(getConfig().kycRedirectUrlNoOk);
    urlNok.searchParams.set('app', 'true');
    urlNok.searchParams.set('locale', language);
    urlNok.searchParams.set('externalId', externalId);

    const request = await gripResource.startIdentification({
      externalId: externalId,
      scenario: getConfig().kycScenario,
      locale: language === 'hu' ? 'hu-HU' : 'en-GB',
      resultRedirectUrlOk: urlOk.toString(),
      resultRedirectUrlNoOk: urlNok.toString(),
    });

    return request.verificationLink || '';
  }, [language]);

  const handleNext = useCallback(async () => {
    if (needsCaptcha && recaptchaInvisible && !captchaToken) {
      waitingForCaptchaRef.current = true;
      await executeInvisibleCaptcha();
      return;
    }

    if (needsCaptcha && !captchaToken) {
      return;
    }

    waitingForCaptchaRef.current = false;
    const url = await startIdentification();
    if (url) {
      window.location.href = url;
    }
  }, [captchaToken, executeInvisibleCaptcha, needsCaptcha, recaptchaInvisible, startIdentification]);

  useEffect(() => {
    if (waitingForCaptchaRef.current && captchaToken) {
      waitingForCaptchaRef.current = false;

      (async () => {
        const url = await startIdentification();
        if (url) {
          window.location.href = url;
        }
      })();
    }
  }, [captchaToken, startIdentification]);

  return (
    <div className={`onboarding-step${isWebView ? ' onboarding-step--webview' : ''}`}>
      <>
        <h4 className="onboarding-step__header">{translation.onboarding?.kyc?.headerApp}</h4>
        <p
          className="onboarding-step__KYCStepText"
          dangerouslySetInnerHTML={{ __html: translation.onboarding?.kyc?.subHeaderApp }}
        />
        <div className="onboarding-step__KYCSteps">
          <p
            className="onboarding-step__KYCStepText"
            dangerouslySetInnerHTML={{ __html: translation.onboarding?.kyc?.stepHeader }}
          />
          <div className="register-page__container__divider"></div>
          <div className="onboarding-step__KYCStep">
            <div>
              <UserIcon className="onboarding-step__info-icon" />
            </div>
            <div>
              <p
                className="onboarding-step__KYCStepText"
                dangerouslySetInnerHTML={{ __html: translation.onboarding?.kyc?.step1 }}
              />
            </div>
          </div>
          <div className="onboarding-step__KYCStep">
            <div>
              <CameraIcon className="onboarding-step__info-icon" />
            </div>
            <div>
              <p
                className="onboarding-step__KYCStepText"
                dangerouslySetInnerHTML={{ __html: translation.onboarding?.kyc?.step2 }}
              />
            </div>
          </div>
          <div className="onboarding-step__KYCStep">
            <div>
              <PhonebookIcon className="onboarding-step__info-icon" />
            </div>
            <div>
              <p
                className="onboarding-step__KYCStepText"
                dangerouslySetInnerHTML={{ __html: translation.onboarding?.kyc?.step3 }}
              />
            </div>
          </div>
          <p
            className="onboarding-step__KYCStepNote"
            dangerouslySetInnerHTML={{ __html: translation.onboarding?.kyc?.stepsNote }}
          />
        </div>

        {/* Yellow warning box */}
        <div className="onboarding-step__warning">
          <WarningIcon className="onboarding-step__warning-icon" />
          <div className="onboarding-step__warning-content">
            <strong>{translation.onboarding?.kyc?.infoHeader}</strong>
          </div>
        </div>

        {needsCaptcha && (
          <RecaptchaPortal
            recaptchaRef={recaptchaRef}
            sitekey={recaptchaSiteKey}
            hl={recaptchaLang}
            onChange={handleRecaptcha}
            className={'verification-step__recaptcha'}
            asyncScriptOnLoad={recaptchaForceRender}
            size={recaptchaInvisible ? 'invisible' : undefined}
          />
        )}
      </>
      {/* Back button */}

      <div className={`onboarding-step__actions${isWebView ? ' onboarding-step__actions--webview' : ''}`}>
        <p
          className="onboarding-step__kyc-description"
          dangerouslySetInnerHTML={{
            __html: translation.onboarding?.kyc?.acceptTerms(privacyUrl),
          }}
        />
        <Button
          variant="contained"
          onClick={handleNext}
        >
          {translation.onboarding?.kyc?.register}
        </Button>
        <Button
          variant="outlined"
          onClick={handleBack}
        >
          {translation.onboarding?.kyc?.backToMethodSelection}
        </Button>
      </div>
    </div>
  );
};
